import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { AdminUser } from '../../services/userManagementService';
import { useSubscriptionTiers } from '../../hooks/useSubscriptionTiers';
import { format } from 'date-fns';

interface UserEditModalProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (userId: string, updates: {
    full_name?: string;
    subscription_tier?: 'free' | 'standard' | 'premium' | 'ultimate';
    admin_role?: string;
    premium_trial_ends_at?: string | null;
    subscription_ends_at?: string | null;
    custom_pet_limit?: number | null;
  }) => void;
  isUpdating: boolean;
}

const UserEditModal: React.FC<UserEditModalProps> = ({
  user,
  isOpen,
  onClose,
  onSave,
  isUpdating
}) => {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState('');
  const [subscriptionTier, setSubscriptionTier] = useState<'free' | 'standard' | 'premium' | 'ultimate'>('free');
  const [adminRole, setAdminRole] = useState('none');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [subscriptionEndsAt, setSubscriptionEndsAt] = useState('');
  const [customPetLimit, setCustomPetLimit] = useState<string>('');
  
  const { data: tiers = [] } = useSubscriptionTiers();

  React.useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setSubscriptionTier(user.subscription_tier);
      setAdminRole(user.admin_role || 'none');
      
      // Load extended fields from user data if available
      const extendedUser = user as any;
      if (extendedUser.premium_trial_ends_at) {
        setTrialEndsAt(format(new Date(extendedUser.premium_trial_ends_at), 'yyyy-MM-dd'));
      } else {
        setTrialEndsAt('');
      }
      if (extendedUser.subscription_ends_at) {
        setSubscriptionEndsAt(format(new Date(extendedUser.subscription_ends_at), 'yyyy-MM-dd'));
      } else {
        setSubscriptionEndsAt('');
      }
      if (extendedUser.custom_pet_limit) {
        setCustomPetLimit(extendedUser.custom_pet_limit.toString());
      } else {
        setCustomPetLimit('');
      }
    }
  }, [user]);

  const handleSave = () => {
    if (!user) return;

    const updates: any = {};
    const extendedUser = user as any;
    
    if (fullName !== user.full_name) {
      updates.full_name = fullName;
    }
    
    if (subscriptionTier !== user.subscription_tier) {
      updates.subscription_tier = subscriptionTier;
    }
    
    if (adminRole !== (user.admin_role || 'none')) {
      updates.admin_role = adminRole;
    }
    
    // Handle trial end date
    const originalTrialDate = extendedUser.premium_trial_ends_at 
      ? format(new Date(extendedUser.premium_trial_ends_at), 'yyyy-MM-dd')
      : '';
    if (trialEndsAt !== originalTrialDate) {
      updates.premium_trial_ends_at = trialEndsAt ? new Date(trialEndsAt).toISOString() : null;
    }
    
    // Handle subscription end date
    const originalSubDate = extendedUser.subscription_ends_at 
      ? format(new Date(extendedUser.subscription_ends_at), 'yyyy-MM-dd')
      : '';
    if (subscriptionEndsAt !== originalSubDate) {
      updates.subscription_ends_at = subscriptionEndsAt ? new Date(subscriptionEndsAt).toISOString() : null;
    }
    
    // Handle custom pet limit
    const originalPetLimit = extendedUser.custom_pet_limit?.toString() || '';
    if (customPetLimit !== originalPetLimit) {
      updates.custom_pet_limit = customPetLimit ? parseInt(customPetLimit) : null;
    }

    onSave(user.id, updates);
    onClose();
  };

  const getTierDisplayName = (tier: string) => {
    return t(`adminPanel.subscriptionTiers.${tier}`, tier);
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('adminPanel.editUser.title')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="email">{t('adminPanel.editUser.email')}</Label>
            <Input id="email" value={user.email} disabled className="bg-muted" />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="fullName">{t('adminPanel.editUser.fullName')}</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label>{t('adminPanel.editUser.subscriptionPlan')}</Label>
            <Select value={subscriptionTier} onValueChange={(value: 'free' | 'standard' | 'premium' | 'ultimate') => setSubscriptionTier(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tiers.length > 0 ? (
                  tiers.map((tier) => (
                    <SelectItem key={tier.tier} value={tier.tier}>
                      {getTierDisplayName(tier.tier)}
                    </SelectItem>
                  ))
                ) : (
                  <>
                    <SelectItem value="free">{t('adminPanel.subscriptionTiers.free')}</SelectItem>
                    <SelectItem value="standard">{t('adminPanel.subscriptionTiers.standard')}</SelectItem>
                    <SelectItem value="premium">{t('adminPanel.subscriptionTiers.premium')}</SelectItem>
                    <SelectItem value="ultimate">{t('adminPanel.subscriptionTiers.ultimate')}</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>{t('adminPanel.editUser.adminRole')}</Label>
            <Select value={adminRole} onValueChange={setAdminRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('adminPanel.adminRoles.none')}</SelectItem>
                <SelectItem value="moderator">{t('adminPanel.adminRoles.moderator')}</SelectItem>
                <SelectItem value="admin">{t('adminPanel.adminRoles.admin')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Extended Admin Controls Section */}
          <div className="border-t pt-4 mt-2">
            <h4 className="text-sm font-semibold mb-3 text-muted-foreground">
              {t('adminPanel.editUser.extendedControls')}
            </h4>
            
            <div className="grid gap-4">
              {/* Trial End Date */}
              <div className="grid gap-2">
                <Label htmlFor="trialEndsAt">{t('adminPanel.editUser.trialEndsAt')}</Label>
                <Input
                  id="trialEndsAt"
                  type="date"
                  value={trialEndsAt}
                  onChange={(e) => setTrialEndsAt(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t('adminPanel.editUser.trialEndsAtHelp')}
                </p>
              </div>

              {/* Subscription End Date */}
              <div className="grid gap-2">
                <Label htmlFor="subscriptionEndsAt">{t('adminPanel.editUser.subscriptionEndsAt')}</Label>
                <Input
                  id="subscriptionEndsAt"
                  type="date"
                  value={subscriptionEndsAt}
                  onChange={(e) => setSubscriptionEndsAt(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t('adminPanel.editUser.subscriptionEndsAtHelp')}
                </p>
              </div>

              {/* Custom Pet Limit */}
              <div className="grid gap-2">
                <Label htmlFor="customPetLimit">{t('adminPanel.editUser.customPetLimit')}</Label>
                <Input
                  id="customPetLimit"
                  type="number"
                  min="1"
                  max="100"
                  placeholder={t('adminPanel.editUser.usesTierDefault')}
                  value={customPetLimit}
                  onChange={(e) => setCustomPetLimit(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t('adminPanel.editUser.customPetLimitHelp')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isUpdating}>
            {isUpdating ? t('adminPanel.editUser.saving') : t('adminPanel.editUser.saveChanges')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserEditModal;
